import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface AdminEventBooking {
  id: string;
  user_id: string;
  quantity: number;
  coupon_code: string | null;
  group_id: string | null;
  created_at: string;
  profiles: {
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
  } | null;
  booking_count: number;
}

export const useAdminEventBookings = (eventId: string | null) => {
  return useQuery({
    queryKey: ['admin-event-bookings', eventId],
    queryFn: async () => {
      if (!eventId) return null;

      // Get tickets with user profiles and booking history
      const { data: tickets, error } = await supabase
        .from('tickets')
        .select(`
          id,
          user_id,
          quantity,
          coupon_code,
          group_id,
          created_at,
          profiles!user_id (
            first_name,
            last_name,
            email,
            phone
          )
        `)
        .eq('event_id', eventId)
        .eq('status', 'confirmed')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Get booking history for each user
      const userIds = tickets?.map(t => t.user_id) || [];
      const { data: bookingCounts, error: countError } = await supabase
        .from('tickets')
        .select('user_id')
        .in('user_id', userIds)
        .eq('status', 'confirmed');

      if (countError) throw countError;

      // Count bookings per user
      const bookingCountMap = bookingCounts?.reduce((acc, booking) => {
        acc[booking.user_id] = (acc[booking.user_id] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      // Combine data
      const enhancedTickets: AdminEventBooking[] = tickets?.map(ticket => ({
        ...ticket,
        booking_count: bookingCountMap[ticket.user_id] || 0
      })) || [];

      return enhancedTickets;
    },
    enabled: !!eventId,
  });
};